import React, { useState } from 'react';

import map from 'lodash.map';

import SocialProofList from './SocialProofList';

import { LazyLoading, usePageContext } from '@xingternal/pages-common';

import { useSocialProofListQuery } from './SocialProofListQuery.gql-types';

import * as Types from '@xingternal/pages-graphql-types';

type SocialProofListContainerProps = {
  pageId: string;
  listTitle: string;
};

const SocialProofListContainer = ({
  pageId,
  listTitle,
}: SocialProofListContainerProps) => {
  const { pageContext } = usePageContext();

  const [fetchMoreError, setFetchMoreError] = useState(false);
  const [lazyLoadingIsAllowed, setLazyLoadingIsAllowed] = useState(true);

  const { data, loading, error, refetch, fetchMore } = useSocialProofListQuery({
    variables: {
      id: pageId,
      limit: 20,
      socialProofClickReasonsKey:
        pageContext.focusType === 'GROUP_PAGE'
          ? Types.ClickReasonsEnum.CrWebGroupsSocialProofHeaderMembers
          : Types.ClickReasonsEnum.CrWebPublisherSocialProofHeader,
    },
    notifyOnNetworkStatusChange: true,
  });

  const getMoreItems = (cursor: string) => {
    fetchMore({
      variables: { limit: 20, after: cursor },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        const newItems =
          fetchMoreResult.entityPageEX?.__typename === 'EntityPage' &&
          fetchMoreResult?.entityPageEX?.userPageContext?.socialProof?.edges;
        setLazyLoadingIsAllowed(newItems ? newItems.length > 0 : false);

        setFetchMoreError(false);

        return fetchMoreResult.entityPageEX?.__typename === 'EntityPage' &&
          prev.entityPageEX?.__typename === 'EntityPage'
          && fetchMoreResult.entityPageEX.userPageContext?.socialProof?.edges
          && prev.entityPageEX.userPageContext?.socialProof?.edges
          ? {
              ...fetchMoreResult,
              entityPageEX: {
                ...fetchMoreResult.entityPageEX,
                userPageContext: {
                  ...fetchMoreResult.entityPageEX.userPageContext,
                  socialProof: {
                    ...fetchMoreResult.entityPageEX.userPageContext?.socialProof,
                    edges: {
                      ...prev.entityPageEX.userPageContext?.socialProof?.edges,
                      ...newItems,
                    },
                  },
                },
              },
            }
          : prev;
      },
    }).catch((err) => {
      if (err) {
        setFetchMoreError(true);
      }
    });
  };

  const socialProof =
    data?.entityPageEX?.__typename === 'EntityPage'
      ? data?.entityPageEX?.userPageContext?.socialProof
      : undefined;

  const userList = map(socialProof?.edges, 'node');
  const cursor = socialProof?.pageInfo?.hasNextPage
    ? socialProof?.pageInfo?.endCursor
    : undefined;

  return (
    <>
      <SocialProofList
        users={userList}
        title={listTitle}
        loading={loading}
        error={error}
        refetch={refetch}
        fetchMore={() => cursor && getMoreItems(cursor)}
        fetchMoreError={fetchMoreError}
      />
      {userList.length > 0 && cursor && !fetchMoreError && (
        <LazyLoading
          type={'LAZY_SCROLL'}
          isLoading={loading}
          enabled={lazyLoadingIsAllowed}
          onLazyLoad={() => getMoreItems(cursor)}
        />
      )}
    </>
  );
};

export default SocialProofListContainer;
